// AI-CONTEXT: See docs/features/agenda-instellingen.md
// De agenda-opties die een organisatie zelf instelt (Instellingen → Ondersteuning
// → 'Agenda'): weekend verbergen, kwartier- of halfuurraster, en welke weergaven
// er in de knoppenbalk staan. Eén plek die de opgeslagen vorm leest, zodat de
// agenda, de instellingen en de handleiding niet uiteenlopen.

import { AGENDA_VIEW_OPTIONS } from './resolveAgendaView'

/**
 * Alle weergaven, in de volgorde van de KNOPPENBALK — die is altijd Lijst,
 * Week, Dag, Maand geweest. De keuzelijst in de instellingen (AGENDA_VIEW_OPTIONS)
 * staat in een andere volgorde; de test hieronder pint vast dat het dezelfde
 * set is, zodat een weergave die daar bijkomt hier niet stil ontbreekt.
 */
const ALL_AGENDA_VIEWS = ['listWeek', 'timeGridWeek', 'timeGridDay', 'dayGridMonth']

/**
 * Hoe het rooster in de agenda staat (alleen met FIXtool, zie scheduleDisplayFor).
 * De waarden zijn wat er in `CompagnyMeta.AgendaScheduleDisplay` komt te staan.
 */
const SCHEDULE_DISPLAY_OPTIONS = [
    { value: 'block', label: 'Blok' },
    { value: 'background', label: 'Achtergrond' },
]

/** De rasterstappen die je kunt kiezen; FullCalendar's eigen standaard is een half uur. */
const SNAP_MINUTES = [15, 30]

/**
 * De knop 'huishoudens' in de knoppenbalk van de pagina Agenda, rechts van het
 * rooster. Geen weergave van FullCalendar maar een ingang naar 'Huishoudens
 * inplannen' (de lijst huishoudens naast de agenda). Dezelfde naam dient als
 * sleutel onder `AgendaViews.Hidden` en als naam van de custom button, zodat
 * de instelling en de knop niet uiteen kunnen lopen.
 */
const HOUSEHOLD_PLANNING_BUTTON = 'householdPlanning'

/**
 * Aan/uit-vlaggen komen in twee vormen voor: ToggleButton schrijft tekst,
 * andere schrijvers booleans. Beide tellen.
 * @param {*} value
 * @returns {boolean}
 */
const aan = (value) => value === true || value === 'true'

/**
 * @typedef {object} AgendaOptions
 * @property {boolean} weekends - Of zaterdag en zondag getekend worden
 * @property {string} snapDuration - Waarop een sleep 'klikt': '00:15:00' of '00:30:00'
 * @property {Array<string>} visibleViews - Weergaven in de knoppenbalk, nooit leeg
 * @property {boolean} householdPlanning - Of de knop 'huishoudens' in de knoppenbalk mag staan
 * @property {'block'|'background'} scheduleDisplay - Rooster als blok of als achtergrond (alleen FIXtool, zie scheduleDisplayFor)
 */

/**
 * @param {object} [meta] - Het CompagnyMeta-document (of de relevante velden ervan)
 * @param {*} [meta.AgendaHideWeekends] - Zaterdag en zondag verbergen
 * @param {*} [meta.AgendaSnapMinutes] - 15 of 30
 * @param {{Hidden?: Record<string, *>}} [meta.AgendaViews] - Per weergave of hij verborgen is
 * @param {*} [meta.AgendaScheduleDisplay] - 'background' of 'block'
 * @returns {AgendaOptions}
 */
const resolveAgendaOptions = (meta) => {
    const m = meta || {}

    const minuten = Number(m.AgendaSnapMinutes)
    const snap = SNAP_MINUTES.includes(minuten) ? minuten : 30

    const hidden = (m.AgendaViews && m.AgendaViews.Hidden) || {}
    const zichtbaar = ALL_AGENDA_VIEWS.filter((view) => !aan(hidden[view]))
    // 'Alles verbergen' wordt genegeerd — zelfde regel als bij de statussen:
    // een agenda zonder één knop heeft ook geen weg terug.
    const visibleViews = zichtbaar.length > 0 ? zichtbaar : ALL_AGENDA_VIEWS

    return {
        weekends: !aan(m.AgendaHideWeekends),
        snapDuration: `00:${String(snap).padStart(2, '0')}:00`,
        visibleViews,
        // Telt niet mee voor 'alles verbergen': zonder deze knop is er nog
        // steeds een agenda met minstens één weergave.
        householdPlanning: !aan(hidden[HOUSEHOLD_PLANNING_BUTTON]),
        // Alleen de exacte tekst: de keuzelijst schrijft niets anders, en elke
        // andere waarde houdt het rooster zoals het altijd was.
        scheduleDisplay: m.AgendaScheduleDisplay === 'background' ? 'background' : 'block',
    }
}

/**
 * De rechterkant van FullCalendar's knoppenbalk: de weergaven, dan het rooster,
 * dan als laatste de knop 'huishoudens' — die is geen weergave maar een ingang
 * naar een ander scherm, en staat daarom aan het eind.
 * @param {Array<string>} visibleViews
 * @param {string} timelineButton - `useTimelineView().toolbarButton`: ',<view>' of ''
 * @param {boolean} [householdButton=false] - Of de knop 'huishoudens' erbij hoort
 * @returns {string}
 */
const buildToolbarRight = (visibleViews, timelineButton, householdButton = false) =>
    `${visibleViews.join(',')}${timelineButton || ''}${householdButton ? `,${HOUSEHOLD_PLANNING_BUTTON}` : ''}`

/**
 * De weergave waarmee de agenda opent, gegeven wat er zichtbaar is.
 *
 * Alleen een van de vier standaardweergaven kan verborgen zijn; een andere
 * beginweergave (de roosterweergave, of wat een aanroeper zelf meegeeft) gaat
 * ongewijzigd door. Is de gevraagde standaardweergave verborgen, dan de eerste
 * zichtbare — anders opent de agenda op een knop die er niet is.
 *
 * @param {string} initialView - Wat de aanroeper vraagt
 * @param {Array<string>} visibleViews - Uit resolveAgendaOptions
 * @returns {string}
 */
const pickInitialView = (initialView, visibleViews) => {
    if (!ALL_AGENDA_VIEWS.includes(initialView)) return initialView
    if (!Array.isArray(visibleViews) || visibleViews.length === 0) return initialView
    return visibleViews.includes(initialView) ? initialView : visibleViews[0]
}

export { ALL_AGENDA_VIEWS, SNAP_MINUTES, SCHEDULE_DISPLAY_OPTIONS, AGENDA_VIEW_OPTIONS, HOUSEHOLD_PLANNING_BUTTON, buildToolbarRight, pickInitialView }
export default resolveAgendaOptions
